import * as React from "react";

import {
  Button as SaltButton,
  SaltIcons,
  Spinner,
  StackLayout,
  Text,
  Toast as SaltToast,
  ToastContent as SaltToastContent,
  type ValidationStatus,
} from "@/design-system/salt";
type ToastId = string | number;

export interface ToastAction {
  label: React.ReactNode;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  description?: React.ReactNode;
  duration?: number;
  id?: ToastId;
}

export interface LegacyToastOptions extends ToastOptions {
  title?: React.ReactNode;
  variant?: "default" | "destructive";
}

interface ToastRecord {
  action?: ToastAction;
  description?: React.ReactNode;
  id: string;
  loading?: boolean;
  status?: ValidationStatus;
  title?: React.ReactNode;
}

interface ToastFunction {
  (
    message: React.ReactNode | LegacyToastOptions,
    options?: ToastOptions,
  ): string;
  dismiss: (id?: ToastId) => void;
  error: (message: React.ReactNode, options?: ToastOptions) => string;
  info: (message: React.ReactNode, options?: ToastOptions) => string;
  loading: (message: React.ReactNode, options?: ToastOptions) => string;
  success: (message: React.ReactNode, options?: ToastOptions) => string;
  warning: (message: React.ReactNode, options?: ToastOptions) => string;
}

const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const emptySnapshot: readonly ToastRecord[] = [];
let records: readonly ToastRecord[] = emptySnapshot;
let nextToastId = 0;

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return records;
}

function normalizeId(id?: ToastId) {
  return id === undefined ? `salt-toast-${++nextToastId}` : String(id);
}

function dismissToast(id?: ToastId) {
  if (id === undefined) {
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    records = emptySnapshot;
    emit();
    return;
  }

  const resolvedId = String(id);
  const timer = timers.get(resolvedId);
  if (timer) clearTimeout(timer);
  timers.delete(resolvedId);
  records = records.filter((record) => record.id !== resolvedId);
  emit();
}

function publishToast(
  title: React.ReactNode,
  options: ToastOptions = {},
  status?: ValidationStatus,
  loading = false,
) {
  const id = normalizeId(options.id);
  const record: ToastRecord = {
    action: options.action,
    description: options.description,
    id,
    loading,
    status,
    title,
  };

  const nextRecords = [
    ...records.filter((current) => current.id !== id),
    record,
  ];
  const evictedRecords = nextRecords.slice(0, -5);
  evictedRecords.forEach((evicted) => {
    const timer = timers.get(evicted.id);
    if (timer) clearTimeout(timer);
    timers.delete(evicted.id);
  });
  records = nextRecords.slice(-5);
  emit();

  const existingTimer = timers.get(id);
  if (existingTimer) clearTimeout(existingTimer);

  const duration = options.duration ?? 5_000;
  if (Number.isFinite(duration) && duration > 0) {
    const timer = setTimeout(() => dismissToast(id), duration);
    if (typeof timer === "object" && "unref" in timer) timer.unref();
    timers.set(id, timer);
  }

  return id;
}

function isLegacyToastOptions(
  value: React.ReactNode | LegacyToastOptions,
): value is LegacyToastOptions {
  return (
    !React.isValidElement(value) &&
    typeof value === "object" &&
    value !== null &&
    ("title" in value || "description" in value || "variant" in value)
  );
}

const toast = ((
  message: React.ReactNode | LegacyToastOptions,
  options?: ToastOptions,
) => {
  if (isLegacyToastOptions(message)) {
    return publishToast(
      message.title,
      message,
      message.variant === "destructive" ? "error" : undefined,
    );
  }

  return publishToast(message, options);
}) as ToastFunction;

toast.success = (message, options) =>
  publishToast(message, options, "success");
toast.error = (message, options) => publishToast(message, options, "error");
toast.info = (message, options) => publishToast(message, options, "info");
toast.loading = (message, options) =>
  publishToast(
    message,
    { ...options, duration: options?.duration ?? Number.POSITIVE_INFINITY },
    undefined,
    true,
  );
toast.warning = (message, options) =>
  publishToast(message, options, "warning");
toast.dismiss = dismissToast;

export interface ToasterProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Global Salt notification host for both legacy object and imperative calls. */
function Toaster({ className, style, ...props }: ToasterProps) {
  const toasts = React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => emptySnapshot,
  );

  return (
    <div
      {...props}
      aria-label="Notifications"
      className={className}
      role="region"
      style={{
        pointerEvents: "none",
        position: "fixed",
        right: "var(--salt-spacing-200)",
        top: "var(--salt-spacing-200)",
        zIndex: "var(--salt-zIndex-notification)",
        display: "flex",
        width: "min(24rem, calc(100vw - var(--salt-spacing-400)))",
        flexDirection: "column",
        gap: "var(--salt-spacing-100)",
        ...style,
      }}
    >
      {toasts.map((notification) => (
        <SaltToast
          data-toast-id={notification.id}
          key={notification.id}
          status={notification.status}
          style={{ pointerEvents: "auto" }}
        >
          {notification.loading ? (
            <Spinner
              aria-label="Loading"
              completionAnnouncement={null}
              disableAnnouncer
              size="small"
            />
          ) : null}
          <SaltToastContent>
            <StackLayout gap={0.5}>
              {notification.title !== undefined ? (
                <Text styleAs="label">{notification.title}</Text>
              ) : null}
              {notification.description !== undefined ? (
                <Text color="secondary" styleAs="notation">
                  {notification.description}
                </Text>
              ) : null}
            </StackLayout>
          </SaltToastContent>
          {notification.action ? (
            <SaltButton
              appearance="bordered"
              onClick={() => {
                notification.action?.onClick();
                dismissToast(notification.id);
              }}
              sentiment="neutral"
              type="button"
            >
              {notification.action.label}
            </SaltButton>
          ) : null}
          <SaltButton
            appearance="transparent"
            aria-label="Dismiss notification"
            onClick={() => dismissToast(notification.id)}
            sentiment="neutral"
            type="button"
          >
            <SaltIcons.CloseIcon aria-hidden="true" />
          </SaltButton>
        </SaltToast>
      ))}
    </div>
  );
}

function useToast() {
  const toasts = React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => emptySnapshot,
  );
  return { dismiss: toast.dismiss, toast, toasts };
}

// This compatibility module deliberately colocates the host and imperative API.
// eslint-disable-next-line react-refresh/only-export-components
export { Toaster, toast, useToast };
